import Modal from 'antd/lib/modal/Modal';
import Button from 'antd/lib/button';
import { useMemo } from 'react';
import { useEnergyUsageDuringEventQuery } from '../../../domain/event-analytic/queries';
import { PerformanceChart, IPerformanceChartData } from './PerformanceChart';
import Flex from 'antd/lib/flex';
import Spin from 'antd/lib/spin';
import { generateExpectedInterval } from './chartUtils';
import { EnergyUsageTimeseriesValueDto, getBaselineCaiso10d, getBaselineStatic } from '../../../domain/energy/interface';
import { BaselineMode } from '../../../domain/energy/baselineMarket';
import { addMinutes } from 'date-fns';
import { isNumber } from 'lodash';
import Empty from 'antd/lib/empty';
import Alert from 'antd/lib/alert';
import App from 'antd/lib/app';
import Typography from 'antd/lib/typography';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import { getComputerFormattedTime } from '../../../domain/date/date';
import './performance-modal.css';

export interface IPerformanceChartModalConfig {
    eventStartUtc: string;
    eventDurationMinutes: number;
    batchId: string;
    nomination: number;
    averageBaseline?: number; // average baseline during event
    companyId?: number;
    siteId?: number;
    siteName?: string;
    meter_count?: number;
    meter?: string;
    /** which baseline to render based on the sites' markets; 'none' hides baseline/target */
    baselineMode?: BaselineMode;
    /** warning shown when baseline/performance can't be aggregated across markets */
    baselineWarning?: string;
}

interface IPerformanceChartModalProps {
    config: IPerformanceChartModalConfig;
    onClose: () => void;
}

export const PerformanceChartModal = ({ onClose, config }: IPerformanceChartModalProps) => {
    const { notification } = App.useApp();

    const handleClose = () => onClose();
    const { data, isFetching, isError, error } = useEnergyUsageDuringEventQuery(
        {
            batchId: config.batchId,
            ...(config.companyId ? { companyId: config.companyId } : {}),
            ...(config.siteId ? { siteId: config.siteId } : {}),
            ...(config.meter ? { san: config.meter } : {}),
            aggregate: true,
            include: 'baseline, performance',
        },
        { enabled: !!config.meter_count }
    );

    if (isError) {
        notification.error({
            key: 'event-analytics-energy-usage-error',
            message: error?.message || 'Cannot fetch usage data for the time of event!',
        });
    }

    const showBaseline = config.baselineMode !== 'none';

    const timezone = getComputerFormattedTime(new Date(), 'xxx');
    const chartTimezone = getComputerFormattedTime(new Date(), 'zzz');

    const preparedData: IPerformanceChartData[] | null = useMemo(() => {
        if (!data || !data[0]?.values?.length || !config) return null;

        const { eventStartUtc, eventDurationMinutes, nomination, baselineMode } = config;
        const getBaseline = baselineMode === 'static' ? getBaselineStatic : getBaselineCaiso10d;
        const eventStartTimestamp = new Date(eventStartUtc).valueOf();
        const eventEndTimestamp = addMinutes(new Date(eventStartUtc), eventDurationMinutes).valueOf();
        const expectedTimestamps: number[] = generateExpectedInterval(new Date(eventStartUtc), eventDurationMinutes, timezone);

        const energyDataIntervals: EnergyUsageTimeseriesValueDto[] = data[0].values;

        const chartIntervals: IPerformanceChartData[] = [];

        expectedTimestamps.forEach(timestamp => {
            let usageKw = null;
            let baselineKw = null;
            let expectedLoadReduction = null;
            let isEventInterval = false;

            const isEventInsideInterval = timestamp >= eventStartTimestamp && timestamp <= eventEndTimestamp;

            if (isEventInsideInterval) isEventInterval = true;

            const matchedInterval = energyDataIntervals.find(item => item[0] === timestamp);

            if (matchedInterval) {
                const [, _usageKw] = matchedInterval;

                usageKw = isNumber(_usageKw) ? _usageKw : null;

                if (baselineMode !== 'none') {
                    const baseline = getBaseline(matchedInterval);
                    baselineKw = isNumber(baseline) ? baseline : null;
                }
            }

            if (isEventInsideInterval && isNumber(baselineKw) && isNumber(nomination)) {
                expectedLoadReduction = baselineKw - nomination;
                if (expectedLoadReduction < 0) expectedLoadReduction = 0;
            }

            chartIntervals.push({
                timestamp,
                baselineKw,
                usageKw,
                isEventInterval,
                nominationKw: expectedLoadReduction,
            });
        });

        return chartIntervals;
    }, [data, config, timezone]);

    return (
        <Modal
            className="performance-chart-modal"
            open
            width={{
                xs: '90%',
                sm: '90%',
                md: '90%',
                lg: '90%',
                xl: '1200px',
                xxl: '1200px',
            }}
            title={getTitle(config)}
            destroyOnHidden
            closeIcon
            onCancel={handleClose}
            styles={{
                footer: {
                    borderTop: 'none',
                    padding: '0 24px 24px 24px',
                },
            }}
            footer={[
                <Button key="modal-cancel" onClick={handleClose}>
                    Close
                </Button>,
            ]}
        >
            <div className="performance-modal-content">
                {!showBaseline && config.baselineWarning && (
                    <Alert type="warning" showIcon style={{ marginBottom: '16px' }} message={config.baselineWarning} />
                )}
                {isFetching && (
                    <Flex style={{ height: '100%' }} align="center" justify="center">
                        <Spin />
                    </Flex>
                )}
                {isError && (
                    <Flex style={{ height: '100%' }} align="center" justify="center">
                        <Empty />
                    </Flex>
                )}
                {preparedData && !isFetching && (
                    <>
                        {chartTimezone && (
                            <Typography.Text style={{ display: 'block', color: 'darkgrey', marginBottom: 12, textAlign: 'center' }}>
                                <InfoCircleOutlined /> Times on this chart are displayed in your computer&apos;s timezone - {chartTimezone}
                            </Typography.Text>
                        )}
                        <PerformanceChart data={preparedData} timezone={timezone} showBaseline={showBaseline} />
                    </>
                )}
            </div>
        </Modal>
    );
};

function getTitle(config: IPerformanceChartModalConfig) {
    const { meter, siteName, meter_count } = config;
    if (meter && siteName) {
        return (
            <span>
                Energy Usage for Meter "{meter}" (Site "{siteName}) During Day of Event`
            </span>
        );
    }
    const totalMeters = meter_count || 0;
    const meterParts = totalMeters > 1 ? `(${totalMeters} meters)` : '';

    if (siteName) {
        return (
            <span>
                Energy Usage for Site "{siteName}" {meterParts} During Day of Event
            </span>
        );
    }

    return <span>Energy Usage During Day of Event {meterParts} </span>;
}
